/**
 * ProfilePictureUpload component.
 * Displays a profile picture with an overlay that lets users
 * pick and upload a new image to Supabase.
 */

import { useRef, useState } from 'react';
import { supabase } from '../utils/supabase';
import '../styles/ProfilePictureUpload.css';

interface ProfilePictureUploadProps {
  currentUrl: string | null;
  userId: string;
  /** Called with the new public URL after a successful upload */
  onUploadComplete: (url: string) => void;
  size?: number;
  fallbackLetter?: string;
}

export default function ProfilePictureUpload({
  currentUrl,
  userId,
  onUploadComplete,
  size = 120,
  fallbackLetter = '?',
}: ProfilePictureUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayUrl = previewUrl || currentUrl;

  /**
   * Opens the file picker dialog.
   */
  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  /**
   * Handles file selection: validates, previews, and uploads.
   */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a PNG, JPEG, WebP, or GIF image.');
      return;
    }

    // Validate file size (max 5 MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5 MB.');
      return;
    }

    setError(null);

    // Show local preview immediately
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    // Determine file extension
    const ext = file.name.split('.').pop()?.toLowerCase() || 'png';

    setIsUploading(true);
    try {
      const { data, error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(`${userId}/avatar.${ext}`, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(data.path);

      // Append timestamp to prevent showing caching of the old image
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      onUploadComplete(publicUrl);
    } catch (err: any) {
      setError(err.message || 'Upload failed. Please try again.');
      // Revert preview on failure
      setPreviewUrl(null);
    } finally {
      setIsUploading(false);
      // Reset file input so re-selecting the same file still triggers onChange
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="ppu-container" onClick={handleClick}>
      <div className="ppu-image-wrapper" style={{ width: size, height: size }}>
        {displayUrl ? (
          <img
            onLoad={(e) => e.currentTarget.classList.add('loaded')}
            src={displayUrl}
            alt="Profile Picture"
            className="ppu-image"
          />
        ) : (
          <div className="ppu-default-avatar">
            {fallbackLetter.charAt(0).toUpperCase()}
          </div>
        )}

        {/* Hover overlay */}
        {!isUploading && (
          <div className="ppu-overlay">
            <span className="ppu-overlay-icon">📷</span>
            <span className="ppu-overlay-text">Change</span>
          </div>
        )}

        {/* Loading overlay */}
        {isUploading && (
          <div className="ppu-loading-overlay">
            <div className="ppu-spinner" />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="ppu-file-input"
        onChange={handleFileChange}
      />

      {error && <div className="ppu-error">{error}</div>}
    </div>
  );
}
